/**
 * Filters for establishments.
 */
import * as React from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/forms/Button';
import { Input } from '@/components/forms/Input';
import { SearchInput } from '@/components/forms/SearchInput';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { DateRangeInput } from '@/components/forms/DateRangeInput';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { getSpecialties } from '@/services/specialties.service';
import { useEstablishmentsAutocomplete } from '@/hooks/useEstablishmentsAutocomplete';
import { ESTABLISHMENT_SEARCH_HINT } from '@/utils/format-establishment-select-label-utils';
import { getEstablishment } from '@/services/establishments.service';
import {
  ESTABLISHMENT_CATEGORY_CONFIG,
  ESTABLISHMENT_PRIORITY_NETWORK_CONFIG,
  ESTABLISHMENT_STATUS_CONFIG,
  PROSPECTION_STATUS_CONFIG,
  ESTABLISHMENT_TYPE_CONFIG,
  type EstablishmentFilters,
} from './establishment-configs';
import {
  mergeEstablishmentAutocompleteOptions,
  parseEstablishmentFilterEndDate,
  parseEstablishmentFilterStartDate,
  stripEstablishmentTrailingParenSuffix,
  toEstablishmentFilterDateString,
} from './establishment-table-utils';
import type {
  EstablishmentCategory,
  EstablishmentPriorityNetwork,
  EstablishmentStatus,
  EstablishmentType,
  ProspectionStatus,
} from '@/types/establishment.types';

/** Props for {@link EstablishmentFiltersContent}. */
interface EstablishmentFiltersContentProps {
  filters: EstablishmentFilters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  onToggleType: (type: string) => void;
  onToggleCategory?: (category: string) => void;
  onTogglePriorityNetwork?: (network: string) => void;
  onToggleStatus: (status: string) => void;
  onToggleProspectionStatus?: (status: string) => void;
  onSetCity?: (city: string | undefined) => void;
  onSetDepartment?: (dept: string | undefined) => void;
  onSetCalendarType?: (type: string | undefined) => void;
  onToggleQpv?: (val: boolean | null) => void;
  onSetSearchQuery?: (query: string) => void;
  onSetQuotaRange?: (min: number | undefined, max: number | undefined) => void;
  onSetEstablishment?: (id: string | undefined, name?: string) => void;
  onSetCustomDateRange?: (from: Date | undefined, to: Date | undefined) => void;
  onSetProspectionValidated?: (val: boolean | null) => void;
  onSetSpecialties?: (ids: string[]) => void;
  onToggleHasDevoirsFaits?: (val: boolean | null) => void;
  onToggleHasInternat?: (val: boolean | null) => void;
  onToggleRenewalNextYear?: (val: boolean | null) => void;
  onToggleConventionSigned?: (val: boolean | null) => void;
  onSetPartnershipDateRange?: (from: Date | undefined, to: Date | undefined) => void;
}

/** Filter group is allowed by RBAC and currently toggled on in the configure panel. */
function isVisible(
  filterId: string,
  allowedFilterIds: string[],
  visibleFilterIds: string[],
): boolean {
  return allowedFilterIds.includes(filterId) && visibleFilterIds.includes(filterId);
}

/** Toggle chips for establishment type. */
const TypeFilter: React.FC<{
  filters: EstablishmentFilters;
  onToggleType: (type: string) => void;
}> = ({ filters, onToggleType }) => (
  <div className="filter-group">
    <span className="filter-group-label">Type</span>
    <div className="filter-options">
      {Object.entries(ESTABLISHMENT_TYPE_CONFIG).map(([key, config]) => {
        const Icon = config.icon;
        const typeKey = key as EstablishmentType;
        const isActive = filters.types.includes(typeKey);
        return (
          <Button
            key={key}
            variant="custom"
            size="sm"
            icon={Icon}
            iconPosition="left"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
              isActive ? 'filter-badge-active' : 'filter-badge-inactive',
            )}
            style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
            onClick={() => onToggleType(key)}
          >
            {config.label}
          </Button>
        );
      })}
    </div>
  </div>
);

/** Toggle chips for public / private category. */
const CategoryFilter: React.FC<{
  filters: EstablishmentFilters;
  onToggleCategory: (category: string) => void;
}> = ({ filters, onToggleCategory }) => (
  <div className="filter-group">
    <span className="filter-group-label">Catégorie</span>
    <div className="filter-options">
      {Object.entries(ESTABLISHMENT_CATEGORY_CONFIG).map(([key, config]) => {
        const Icon = config.icon;
        const categoryKey = key as EstablishmentCategory;
        const isActive = (filters.categories ?? []).includes(categoryKey);
        return (
          <Button
            key={key}
            variant="custom"
            size="sm"
            icon={Icon}
            iconPosition="left"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
              isActive ? 'filter-badge-active' : 'filter-badge-inactive',
            )}
            style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
            onClick={() => onToggleCategory(key)}
          >
            {config.label}
          </Button>
        );
      })}
    </div>
  </div>
);

/** Toggle chips for priority-education network. */
const PriorityNetworkFilter: React.FC<{
  filters: EstablishmentFilters;
  onTogglePriorityNetwork: (network: string) => void;
}> = ({ filters, onTogglePriorityNetwork }) => (
  <div className="filter-group">
    <span className="filter-group-label">Réseau prioritaire</span>
    <div className="filter-options">
      {Object.entries(ESTABLISHMENT_PRIORITY_NETWORK_CONFIG).map(([key, config]) => {
        const Icon = config.icon;
        const networkKey = key as EstablishmentPriorityNetwork;
        const isActive = (filters.priorityNetworks ?? []).includes(networkKey);
        return (
          <Button
            key={key}
            variant="custom"
            size="sm"
            icon={Icon}
            iconPosition="left"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
              isActive ? 'filter-badge-active' : 'filter-badge-inactive',
            )}
            style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
            onClick={() => onTogglePriorityNetwork(key)}
          >
            {config.label}
          </Button>
        );
      })}
    </div>
  </div>
);

/** Toggle chips for establishment status. */
const StatusFilter: React.FC<{
  filters: EstablishmentFilters;
  onToggleStatus: (status: string) => void;
}> = ({ filters, onToggleStatus }) => (
  <div className="filter-group">
    <span className="filter-group-label">Statut</span>
    <div className="filter-options">
      {Object.entries(ESTABLISHMENT_STATUS_CONFIG).map(([key, config]) => {
        const Icon = config.icon;
        const statusKey = key as EstablishmentStatus;
        const isActive = filters.statuses.includes(statusKey);
        return (
          <Button
            key={key}
            variant="custom"
            size="sm"
            icon={Icon}
            iconPosition="left"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
              isActive ? 'filter-badge-active' : 'filter-badge-inactive',
            )}
            style={
              {
                '--filter-color': config.badgeColor ?? 'rgb(var(--status-success))',
              } as React.CSSProperties
            }
            onClick={() => onToggleStatus(key)}
          >
            {config.label}
          </Button>
        );
      })}
    </div>
  </div>
);

/** Toggle chips for prospection status. */
const ProspectionStatusFilter: React.FC<{
  filters: EstablishmentFilters;
  onToggleProspectionStatus: (status: string) => void;
}> = ({ filters, onToggleProspectionStatus }) => (
  <div className="filter-group">
    <span className="filter-group-label">Statut de prospection</span>
    <div className="filter-options">
      {Object.entries(PROSPECTION_STATUS_CONFIG).map(([key, config]) => {
        const Icon = config.icon;
        const statusKey = key as ProspectionStatus;
        const isActive = filters.prospectionStatuses.includes(statusKey);
        return (
          <Button
            key={key}
            variant="custom"
            size="sm"
            icon={Icon}
            iconPosition="left"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
              isActive ? 'filter-badge-active' : 'filter-badge-inactive',
            )}
            style={
              {
                '--filter-color': config.badgeColor ?? 'rgb(var(--status-neutral))',
              } as React.CSSProperties
            }
            onClick={() => onToggleProspectionStatus(key)}
          >
            {config.label}
          </Button>
        );
      })}
    </div>
  </div>
);

/** {@link Autocomplete} by name; selected id not in the first page is fetched so the label stays visible. */
const EstablishmentSearchFilter: React.FC<{
  filters: EstablishmentFilters;
  onSetEstablishment: (id: string | undefined, name?: string) => void;
}> = ({ filters, onSetEstablishment }) => {
  const { options, isLoading, onSearch, onLoadMore, hasMore } = useEstablishmentsAutocomplete();

  const { data: selectedEstablishment } = useQuery({
    queryKey: ['establishment', filters.selectedEstablishmentId],
    queryFn: () => getEstablishment(filters.selectedEstablishmentId!),
    enabled: Boolean(filters.selectedEstablishmentId),
  });

  const mergedOptions = React.useMemo(
    () =>
      mergeEstablishmentAutocompleteOptions(
        options,
        filters.selectedEstablishmentId,
        selectedEstablishment,
      ),
    [filters.selectedEstablishmentId, options, selectedEstablishment],
  );

  /** Store id + stripped label (autocomplete suffix is display-only). */
  const handleChange = (id: string) => {
    if (!id) {
      onSetEstablishment(undefined);
      return;
    }
    const opt = mergedOptions.find((o) => o.value === id);
    const name = opt ? stripEstablishmentTrailingParenSuffix(opt.label) : '';
    onSetEstablishment(id, name);
  };

  return (
    <div className="filter-group">
      <span className="filter-group-label">Établissement</span>
      <div className="w-64">
        <Autocomplete
          placeholder="Rechercher un établissement..."
          options={mergedOptions}
          value={filters.selectedEstablishmentId ?? ''}
          onChange={handleChange}
          onSearch={onSearch}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          minSearchLength={2}
          helperText={ESTABLISHMENT_SEARCH_HINT}
          inputSize="sm"
        />
      </div>
    </div>
  );
};

/** Free-text city filter (single value stored as `cities[0]`). */
const CitiesFilter: React.FC<{
  filters: EstablishmentFilters;
  onSetCity: (city: string | undefined) => void;
}> = ({ filters, onSetCity }) => {
  const selectedCity = filters.cities?.[0];

  return (
    <div className="filter-group">
      <span className="filter-group-label">Ville</span>
      <div className="w-64">
        <SearchInput
          size="sm"
          shortcut={false}
          placeholder="Filtrer par ville..."
          value={selectedCity ?? ''}
          onChange={(e) => onSetCity(e.target.value || undefined)}
          onClear={() => onSetCity(undefined)}
        />
      </div>
    </div>
  );
};

/** Free-text department code filter (single value stored as `departments[0]`). */
const DepartmentsFilter: React.FC<{
  filters: EstablishmentFilters;
  onSetDepartment: (dept: string | undefined) => void;
}> = ({ filters, onSetDepartment }) => {
  const selectedDept = filters.departments?.[0];

  return (
    <div className="filter-group">
      <span className="filter-group-label">Département</span>
      <div className="w-48">
        <SearchInput
          size="sm"
          shortcut={false}
          placeholder="Ex: 75, 93..."
          value={selectedDept ?? ''}
          onChange={(e) => onSetDepartment(e.target.value || undefined)}
          onClear={() => onSetDepartment(undefined)}
        />
      </div>
    </div>
  );
};

/** Exclusive zone-A/B/C chip; clicking the active chip clears it. */
const CalendarTypeFilter: React.FC<{
  filters: EstablishmentFilters;
  onSetCalendarType: (type: string | undefined) => void;
}> = ({ filters, onSetCalendarType }) => {
  const CALENDAR_TYPES = [
    { value: 'zone_a', label: 'Zone A' },
    { value: 'zone_b', label: 'Zone B' },
    { value: 'zone_c', label: 'Zone C' },
  ];

  const selectedType = filters.calendarTypes?.[0];

  return (
    <div className="filter-group">
      <span className="filter-group-label">Calendrier</span>
      <div className="filter-options">
        {CALENDAR_TYPES.map((cal) => {
          const isActive = selectedType === cal.value;
          return (
            <Button
              key={cal.value}
              variant="custom"
              size="sm"
              icon={CalendarIcon}
              iconPosition="left"
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                isActive ? 'filter-option-primary--active' : 'filter-option-primary',
              )}
              onClick={() => onSetCalendarType(isActive ? undefined : cal.value)}
            >
              {cal.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

/** Three-state yes / no chip (`null` = unset). */
const YesNoFilter: React.FC<{
  label: string;
  value?: boolean | null;
  onChange: (val: boolean | null) => void;
}> = ({ label, value, onChange }) => (
  <div className="filter-group">
    <span className="filter-group-label">{label}</span>
    <div className="filter-options">
      <Button
        preset="filter-yes"
        active={value === true}
        onClick={() => onChange(value === true ? null : true)}
      >
        Oui
      </Button>
      <Button
        preset="filter-no"
        active={value === false}
        onClick={() => onChange(value === false ? null : false)}
      >
        Non
      </Button>
    </div>
  </div>
);

/** QPV yes/no wrapper around {@link YesNoFilter}. */
const QpvFilter: React.FC<{
  filters: EstablishmentFilters;
  onToggleQpv: (val: boolean | null) => void;
}> = ({ filters, onToggleQpv }) => (
  <YesNoFilter label="QPV" value={filters.isQpv} onChange={onToggleQpv} />
);

/** Partnership date range; end-of-day is set to 23:59:59 so the last day is inclusive. */
const PartnershipPeriodFilter: React.FC<{
  filters: EstablishmentFilters;
  onSetPartnershipDateRange: (from: Date | undefined, to: Date | undefined) => void;
}> = ({ filters, onSetPartnershipDateRange }) => {
  const fromStr = toEstablishmentFilterDateString(filters.partnershipDateFrom);
  const toStr = toEstablishmentFilterDateString(filters.partnershipDateTo);

  const handleFromChange = (val: string) => {
    if (!val) {
      onSetPartnershipDateRange(undefined, filters.partnershipDateTo);
      return;
    }
    onSetPartnershipDateRange(parseEstablishmentFilterStartDate(val), filters.partnershipDateTo);
  };

  const handleToChange = (val: string) => {
    if (!val) {
      onSetPartnershipDateRange(filters.partnershipDateFrom, undefined);
      return;
    }
    onSetPartnershipDateRange(filters.partnershipDateFrom, parseEstablishmentFilterEndDate(val));
  };

  return (
    <div className="filter-group">
      <span className="filter-group-label">Période de partenariat</span>
      <div className="w-auto">
        <DateRangeInput
          startDate={fromStr}
          endDate={toStr}
          onStartDateChange={handleFromChange}
          onEndDateChange={handleToChange}
          layout="horizontal"
          showConnector={true}
          showPresets={false}
          hidePickerLabels={true}
          compact={true}
        />
      </div>
    </div>
  );
};

/** Created-at date range; end-of-day is set to 23:59:59 so the last day is inclusive. */
const CreationDateFilter: React.FC<{
  filters: EstablishmentFilters;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
}> = ({ filters, onSetCustomDateRange }) => {
  const fromStr = toEstablishmentFilterDateString(filters.createdFrom);
  const toStr = toEstablishmentFilterDateString(filters.createdTo);

  const handleFromChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(undefined, filters.createdTo);
      return;
    }
    onSetCustomDateRange(parseEstablishmentFilterStartDate(val), filters.createdTo);
  };

  const handleToChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(filters.createdFrom, undefined);
      return;
    }
    onSetCustomDateRange(filters.createdFrom, parseEstablishmentFilterEndDate(val));
  };

  return (
    <div className="filter-group">
      <span className="filter-group-label">Date de création</span>
      <div className="w-auto">
        <DateRangeInput
          startDate={fromStr}
          endDate={toStr}
          onStartDateChange={handleFromChange}
          onEndDateChange={handleToChange}
          layout="horizontal"
          showConnector={true}
          showPresets={false}
          hidePickerLabels={true}
          compact={true}
        />
      </div>
    </div>
  );
};

/** "Managed by" filter — wired to `isProspectionValidated` (legacy filter id). */
const ManagerFilter: React.FC<{
  filters: EstablishmentFilters;
  onSetProspectionValidated: (val: boolean | null) => void;
}> = ({ filters, onSetProspectionValidated }) => (
  <YesNoFilter
    label="Géré par"
    value={filters.isProspectionValidated}
    onChange={onSetProspectionValidated}
  />
);

/** Free-text search across establishment fields. */
const SearchFilter: React.FC<{
  filters: EstablishmentFilters;
  onSetSearchQuery: (query: string) => void;
}> = ({ filters, onSetSearchQuery }) => (
  <div className="filter-group">
    <span className="filter-group-label">Recherche</span>
    <div className="w-64">
      <SearchInput
        size="sm"
        shortcut={false}
        placeholder="Rechercher..."
        value={filters.searchQuery ?? ''}
        onChange={(e) => onSetSearchQuery(e.target.value)}
        onClear={() => onSetSearchQuery('')}
      />
    </div>
  </div>
);

/** Multi-select of active specialties. */
const SpecialtyFilter: React.FC<{
  filters: EstablishmentFilters;
  onSetSpecialties: (ids: string[]) => void;
}> = ({ filters, onSetSpecialties }) => {
  const { data: specialties = [], isLoading } = useQuery({
    queryKey: ['specialties', 'active'],
    queryFn: () => getSpecialties(true),
  });

  const options = specialties.map((s) => ({ value: s.id, label: s.label }));

  return (
    <div className="filter-group">
      <span className="filter-group-label">Spécialités</span>
      <div className="w-64">
        <MultiSelect
          options={options}
          value={filters.specialtyIds ?? []}
          onChange={onSetSpecialties}
          placeholder={isLoading ? 'Chargement...' : 'Toutes les spécialités'}
          inputSize="sm"
        />
      </div>
    </div>
  );
};

/** Min / max quota range (empty input = unbounded). */
const QuotasFilter: React.FC<{
  filters: EstablishmentFilters;
  onSetQuotaRange: (min: number | undefined, max: number | undefined) => void;
}> = ({ filters, onSetQuotaRange }) => (
  <div className="filter-group">
    <span className="filter-group-label">Quotas</span>
    <div className="flex items-center gap-2">
      <Input
        type="number"
        inputSize="sm"
        placeholder="Min"
        className="w-24"
        value={filters.quotaMin ?? ''}
        onChange={(e) =>
          onSetQuotaRange(e.target.value ? Number(e.target.value) : undefined, filters.quotaMax)
        }
      />
      <span className="text-sm text-muted-foreground">—</span>
      <Input
        type="number"
        inputSize="sm"
        placeholder="Max"
        className="w-24"
        value={filters.quotaMax ?? ''}
        onChange={(e) =>
          onSetQuotaRange(filters.quotaMin, e.target.value ? Number(e.target.value) : undefined)
        }
      />
    </div>
  </div>
);

/** Ribbon of establishment filters; each group is gated by RBAC + configure-panel visibility. */
export const EstablishmentFiltersContent: React.FC<EstablishmentFiltersContentProps> = ({
  filters,
  allowedFilterIds,
  visibleFilterIds,
  onToggleType,
  onToggleCategory,
  onTogglePriorityNetwork,
  onToggleStatus,
  onToggleProspectionStatus,
  onSetCity,
  onSetDepartment,
  onSetCalendarType,
  onToggleQpv,
  onSetSearchQuery,
  onSetQuotaRange,
  onSetEstablishment,
  onSetCustomDateRange,
  onSetProspectionValidated,
  onSetSpecialties,
  onToggleHasDevoirsFaits,
  onToggleHasInternat,
  onToggleRenewalNextYear,
  onToggleConventionSigned,
  onSetPartnershipDateRange,
}) => {
  const show = (id: string) => isVisible(id, allowedFilterIds, visibleFilterIds);

  return (
    <div className="filter-groups-container">
      {show('establishment-search') && onSetEstablishment && (
        <EstablishmentSearchFilter filters={filters} onSetEstablishment={onSetEstablishment} />
      )}
      {show('establishment-types') && <TypeFilter filters={filters} onToggleType={onToggleType} />}
      {show('establishment-categories') && onToggleCategory && (
        <CategoryFilter filters={filters} onToggleCategory={onToggleCategory} />
      )}
      {show('establishment-priority-networks') && onTogglePriorityNetwork && (
        <PriorityNetworkFilter
          filters={filters}
          onTogglePriorityNetwork={onTogglePriorityNetwork}
        />
      )}
      {show('establishment-qpv') && onToggleQpv && (
        <QpvFilter filters={filters} onToggleQpv={onToggleQpv} />
      )}
      {show('establishment-statuses') && (
        <StatusFilter filters={filters} onToggleStatus={onToggleStatus} />
      )}
      {show('establishment-prospection-statuses') && onToggleProspectionStatus && (
        <ProspectionStatusFilter
          filters={filters}
          onToggleProspectionStatus={onToggleProspectionStatus}
        />
      )}
      {show('establishment-cities') && onSetCity && (
        <CitiesFilter filters={filters} onSetCity={onSetCity} />
      )}
      {show('establishment-departments') && onSetDepartment && (
        <DepartmentsFilter filters={filters} onSetDepartment={onSetDepartment} />
      )}
      {show('establishment-calendar') && onSetCalendarType && (
        <CalendarTypeFilter filters={filters} onSetCalendarType={onSetCalendarType} />
      )}
      {show('establishment-search') && onSetSearchQuery && (
        <SearchFilter filters={filters} onSetSearchQuery={onSetSearchQuery} />
      )}
      {show('establishment-quotas') && onSetQuotaRange && (
        <QuotasFilter filters={filters} onSetQuotaRange={onSetQuotaRange} />
      )}
      {show('filter-managed-by') && onSetProspectionValidated && (
        <ManagerFilter filters={filters} onSetProspectionValidated={onSetProspectionValidated} />
      )}
      {show('establishment-convention-signed') && onToggleConventionSigned && (
        <YesNoFilter
          label="Convention signée"
          value={filters.conventionSigned}
          onChange={onToggleConventionSigned}
        />
      )}
      {show('establishment-dates') && onSetCustomDateRange && (
        <CreationDateFilter filters={filters} onSetCustomDateRange={onSetCustomDateRange} />
      )}
      {show('establishment-specialties') && onSetSpecialties && (
        <SpecialtyFilter filters={filters} onSetSpecialties={onSetSpecialties} />
      )}
      {show('establishment-homework-support') && onToggleHasDevoirsFaits && (
        <YesNoFilter
          label="Devoirs Faits"
          value={filters.hasDevoirsFaits}
          onChange={onToggleHasDevoirsFaits}
        />
      )}
      {show('establishment-boarding') && onToggleHasInternat && (
        <YesNoFilter label="Internat" value={filters.hasInternat} onChange={onToggleHasInternat} />
      )}
      {show('establishment-partnership-period') && onSetPartnershipDateRange && (
        <PartnershipPeriodFilter
          filters={filters}
          onSetPartnershipDateRange={onSetPartnershipDateRange}
        />
      )}
      {show('establishment-renewal-next-year') && onToggleRenewalNextYear && (
        <YesNoFilter
          label="Reconduction N+1"
          value={filters.renewalNextYear}
          onChange={onToggleRenewalNextYear}
        />
      )}
    </div>
  );
};
